import { useEffect, useState } from 'react';
import { useDmMessagePatient } from '../../../hooks/useDmMessagesInbox';
import type {
    DmMessageInboxFilters,
    DmMessagePatientDetail,
    DmMessagePatientRow
} from '../../../services/dmMessagesInbox';
import { DEFAULT_MESSAGE_SORT } from './messageListParams';
import type { TriageControlsValue } from './triage/TriageControls';

// Only its patient block is read; the neighbours these filters drive are ignored.
export const PATIENT_LOOKUP_FILTERS: DmMessageInboxFilters = {
    sort: DEFAULT_MESSAGE_SORT,
    order: 'desc'
};

// Prefers the live list row, then the patient endpoint, then the last listed copy; the endpoint
// always loads since only it carries the AI's suggested reply.
export function useDmMessagePanelPatient(patientId: string, listed: DmMessagePatientRow | null) {
    const [lastListed, setLastListed] = useState<DmMessagePatientRow | null>(listed);
    useEffect(() => {
        if (listed) setLastListed(listed);
    }, [listed]);
    const seen = lastListed?.patientId === patientId ? lastListed : null;

    const lookup = useDmMessagePatient(patientId, PATIENT_LOOKUP_FILTERS);
    const looked = lookup.data?.patient ?? null;
    const patient: Omit<DmMessagePatientDetail, 'triage'> | null = listed ?? looked ?? seen;
    // Unknown (null) only while nothing has loaded yet.
    const unreviewedCount = listed?.unreviewedCount ?? looked?.unreviewedCount ?? (seen ? 0 : null);
    // Levels from the live row; set-by details only when the patient lookup has loaded.
    const triage: TriageControlsValue | null = listed
        ? { ...looked?.triage, ...listed.triage }
        : (looked?.triage ?? seen?.triage ?? null);

    return {
        patient,
        unreviewedCount,
        triage,
        detail: looked,
        isError: patient === null && lookup.isError
    };
}
